import { useState, useEffect } from 'react';
import { Avatar, Box, Button, TextField, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useParams } from 'react-router';
import { requestApi } from 'src/api/requestApi';
import toast from 'react-hot-toast';
import {
  ClassicEditor,
  AutoImage,
  BlockQuote,
  Bold,
  Essentials,
  Heading,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Paragraph,
  SimpleUploadAdapter,
  TextTransformation,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { userApi } from 'src/api/userApi';
import { CKEditor } from '@ckeditor/ckeditor5-react';

const useStyles = makeStyles<Theme>(() =>
  createStyles({
    root: {
      marginTop: '12px',
      '& .userInfo': {
        display: 'flex',
        alignItems: 'center',
        '& .MuiTypography-root': {
          marginLeft: '10px',
          fontSize: '12px',
        },
      },
      '& .requestReason': {
        marginTop: '12px',
        '& h4': {
          color: '#423636',
          fontSize: '14px',
        },
        '& .MuiTypography-root': {
          fontSize: '12px',
        },
      },
    },
    avatar: {
      width: '30px',
      height: '30px',
    },
    descriptionText: {
      cursor: 'pointer',
      fontSize: '12px',
      color: '#000',
    },
    textField: {
      width: '100%',
      marginTop: '8px',
    },
    button: {
      marginTop: '12px',
      fontSize: '12px',
      marginRight: '8px', // Add some spacing between buttons
    },
  })
);

type RequestInfoTypes = {
  creater: string;
  date: string;
  answer: string;
};

export const RequestInfo = ({ creater, date, answer }: RequestInfoTypes) => {
  const classes = useStyles();
  const { requestID } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [description, setDescription] = useState(answer);
  const [originalDescription, setOriginalDescription] = useState(answer);

  // Sync description with answer prop when the prop changes
  useEffect(() => {
    setDescription(answer);
    setOriginalDescription(answer); // Keep track of original description
  }, [answer, 0]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setDescription(originalDescription); // Revert changes
    setIsEditing(false); // Close editing mode
  };

  const handleUpdateClick = async () => {
    try {
      await requestApi.updateHrDescription(requestID, { description });
      toast.success('Description updated successfully');
      setOriginalDescription(description); // Update the original description
      setIsEditing(false);
    } catch (err) {
      toast.error(err?.message || 'Something went wrong');
    }
  };

  function uploadAdapter(loader) {
    return {
      upload: () =>
        new Promise((resolve) => {
          loader.file.then(async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            const image = await userApi.uploadAttachments(formData);
            resolve({
              default: `${image?.data?.blob_url}`,
            });
          });
        }),
    };
  }
  function uploadPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) =>
      uploadAdapter(loader);
  }

  const editorConfig = {
    toolbar: {
      items: [
        '|',
        'heading',
        '|',
        'bold',
        'italic',
        '|',
        'link',
        'blockQuote',
        'insertImage',
        '|',
        'bulletedList',
        'numberedList',
      ],
      shouldNotGroupWhenFull: false,
    },
    plugins: [
      AutoImage,
      BlockQuote,
      Bold,
      Essentials,
      Heading,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      Link,
      List,
      ListProperties,
      Paragraph,
      SimpleUploadAdapter,
      TextTransformation,
    ],
    blockToolbar: [
      'bold',
      'italic',
      '|',
      'link',
      '|',
      'bulletedList',
      'numberedList',
      'outdent',
      'indent',
    ],
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph',
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_heading1',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_heading2',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_heading3',
        },
      ],
    },
    image: {
      toolbar: [
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'imageStyle:inline',
        'imageStyle:wrapText',
        'imageStyle:breakText',
        '|',
        'resizeImage',
      ],
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: {
            download: 'file',
          },
        },
      },
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    },

    extraPlugins: [uploadPlugin],
    placeholder: 'Enter your description here',
  };

  return (
    <div className={classes.root}>
      <Box className="userInfo">
        <Avatar src="assets/images/avatar.png" className={classes.avatar} />
        <Typography>
          <span style={{ fontWeight: 'bold' }}>{creater}</span> raised this on{' '}
          {moment(date).format('LL')}
        </Typography>
      </Box>
      <Box className="requestReason">
        {!isEditing ? (
          <div
            className={classes.descriptionText}
            onClick={handleEditClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleEditClick();
              }
            }}
            role="button"
            tabIndex={0}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: description || 'Click to add a description',
            }}
            aria-label="Edit the description"
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <>
            <CKEditor
              editor={ClassicEditor}
              data={description}
              onChange={(event, editor) => {
                const data = editor.getData();
                setDescription(data);
              }}
              config={editorConfig as any}
            />
            <Button
              className={classes.button}
              color="secondary"
              variant="contained"
              onClick={handleUpdateClick}>
              Update
            </Button>
            <Button
              className={classes.button}
              color="secondary"
              variant="outlined"
              onClick={handleCancelClick}>
              Cancel
            </Button>
          </>
        )}
      </Box>
    </div>
  );
};
